import { useGetGeslotenDossierOverzicht } from "@/entities/dossier-overzicht";
import { SkeletonDossierCard } from "@/features/dossier-balk/components/ui";
import { getReikwijdteLabel } from "@/features/dossier-details";
import { Empty, EmptyDescription, EmptyImage, ErrorDisplay, SearchInput } from "@/shared/components";
import { useDebouncedValue } from "@/shared/hooks/use-debounced-value";
import { useScrollEnd } from "@/shared/hooks/use-scroll-end.ts";
import { useCallback, useState } from "react";
import { DossierCard } from "./dossier-card.tsx";

export function AfgerondDossierList() {
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebouncedValue(search, 500);
    const { data: filteredDossiers, isLoading, isFetchingNextPage, error, fetchNextPage } = useGetGeslotenDossierOverzicht(debouncedSearch);

    const onEnd = useCallback(() => void fetchNextPage(), [fetchNextPage]);
    const ref = useScrollEnd<HTMLOListElement>(onEnd);

    return (
        <>
            <div className="px-4 pt-3">
                <SearchInput search={search} setSearch={setSearch} className="max-w-[326px] pb-0.5" />
            </div>
            <div className="px-m bg-elevated-1 shadow-elevated-2 gap-m relative z-20 flex min-h-[96px] w-full max-w-screen items-center overflow-y-hidden">
                <ol
                    className="gap-s relative flex flex-row overflow-x-auto overflow-y-hidden pt-2 pb-3"
                    data-testid="afgerond-dossiers-list"
                    ref={ref}
                >
                    {error && (
                        <ErrorDisplay
                            error={error}
                            onRetry={() => window.location.reload()}
                            title="Fout bij het laden van afgeronde dossiers"
                        />
                    )}
                    {isLoading ? (
                        <SkeletonDossierCard testId="afgerond-dossier-skeleton" />
                    ) : filteredDossiers?.length === 0 ? (
                        <Empty className="max-h-[82px] flex-row">
                            <EmptyImage variant="search" className="[&>svg]:size-18" />
                            <EmptyDescription>Geen dossiers gevonden</EmptyDescription>
                        </Empty>
                    ) : (
                        <>
                            {filteredDossiers?.map((dossier) => (
                                <DossierCard
                                    key={dossier.dossierId}
                                    dossierId={dossier.dossierId.toString()}
                                    seinStartDatum={dossier.startDatumTijd}
                                    omschrijving={dossier.omschrijving}
                                    reikwijdteLabel={getReikwijdteLabel(dossier.reikwijdte, dossier.stations, dossier.trajecten)}
                                    uitgezetteDiensten={dossier.uitgezetteDiensten}
                                    acties={dossier.acties}
                                    suggesties={dossier.suggesties}
                                    betrokkenTreinen={dossier.betrokkenTreinen}
                                    linkPrefix="/afgerond/dossier"
                                />
                            ))}
                            {isFetchingNextPage && <SkeletonDossierCard testId="afgerond-dossier-skeleton-loading-more" />}
                        </>
                    )}
                </ol>
            </div>
        </>
    );
}
